import { stripeExpressContract } from "@common/contracts/stripe-express.contract";
import {
    StripeExpressBranding,
    StripeExpressUpdateBrandingBody,
} from "@common/schemas/domains/stripe/stripe-express.schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBackendAPIClient } from "@lib/backendAPIClient";

const stripeExpressClient = getBackendAPIClient(stripeExpressContract);

export function stripeExpressBrandingQueryKey(connectionId: number) {
    return ["stripe-express-branding", connectionId] as const;
}

export function useStripeExpressBrandingQuery({
    connectionId,
    isEnabled,
}: {
    connectionId: number;
    isEnabled: boolean;
}) {
    return useQuery({
        queryKey: stripeExpressBrandingQueryKey(connectionId),
        enabled: isEnabled,
        refetchOnWindowFocus: false,
        queryFn: async (): Promise<StripeExpressBranding> => {
            const result = await stripeExpressClient.getBranding({
                params: { connectionId },
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to load Stripe Express branding (status ${result.status})`,
                );
            }
            return result.body;
        },
    });
}

export function useUpdateStripeExpressBrandingMutation(connectionId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            body: StripeExpressUpdateBrandingBody,
        ): Promise<StripeExpressBranding> => {
            const result = await stripeExpressClient.updateBranding({
                params: { connectionId },
                body,
            });
            if (result.status !== 200) {
                throw new Error(
                    `Failed to save Stripe Express branding (status ${result.status})`,
                );
            }
            return result.body;
        },
        onSuccess: (branding) => {
            queryClient.setQueryData(
                stripeExpressBrandingQueryKey(connectionId),
                branding,
            );
        },
    });
}
